/** @file Drags an IK target bone (the solve previews live); commits the target plus optional extras. */
import { parentSpace } from '@pose2d/editor-geometry/parentSpace';
import type { Id } from '@pose2d/format/types';
import type { PreviewValues } from '../../panels/viewport/scene/channelOffset';
import type { ToolContext, ToolGesture, ViewportPointer } from '../../panels/viewport/viewportTypes';
import { worldAt } from '../../panels/viewport/worldAt';
import { boneOrigin } from './boneOrigin';
import type { EditTarget } from './editTargetOf';
import type { IkRef } from './ikRef';
import { parentWorldOf } from './parentWorldOf';
import { snappedWorld } from './snappedWorld';
import { transformCommands } from './transformCommands';

/**
 * Extra values committed after the target, read on every move after a re-pose (solved values are current), so a
 * stateful extra can unwrap a rotation next to the previous move's (chainRotations).
 */
export type IkExtras = (ctx: ToolContext, edit: EditTarget) => Map<Id, PreviewValues>;

/** null (notice viewport.socketBone) when the target is a socket bone (R-E1-09); a click without a move commits nothing. */
export function ikTargetGesture(ctx: ToolContext, edit: EditTarget, ik: IkRef, press: ViewportPointer, extras?: IkExtras): ToolGesture | null {
  if (ctx.data.bones[ik.target]?.kind !== 'bone') {
    ctx.notify({ kind: 'info', key: 'viewport.socketBone' });
    return null;
  }
  const id = ctx.maps.boneIds[ik.target] ?? '';
  const origin = boneOrigin(ctx.pose, ik.target);
  const parent = parentWorldOf(ctx.pose, ctx.data, ik.target);
  const start = worldAt(ctx, press);
  let values = new Map<Id, PreviewValues>();
  let extra = new Map<Id, PreviewValues>();
  return {
    move(p) {
      const w = worldAt(ctx, p);
      const moved = snappedWorld(ctx, p, [origin[0] + w[0] - start[0], origin[1] + w[1] - start[1]]);
      const local = parentSpace.point(parent, moved[0], moved[1], [0, 0]);
      values = new Map([[id, { x: local[0], y: local[1] }]]);
      ctx.setPreview(values);
      if (extras === undefined) return;
      ctx.repose();
      extra = extras(ctx, edit);
    },
    release: () => (values.size === 0 ? [] : transformCommands(edit, new Map([...values, ...extra]))),
    cancel() {},
  };
}
